/**
 * ToolProgressStatus — ephemeral, collapsible status line for slow
 * tool calls.
 *
 * Renders the `tool_progress` SSE frame defined in W16 / TIME-198. The
 * supervisor emits these frames while a long-running tool (e.g.
 * `db_ask`) blocks the streamText loop, so the user sees
 * lifecycle progress instead of dead air. The component is purely a
 * presenter — useChatV2 owns the lifecycle state and drives
 * mount/unmount on terminal status (`completed` / `failed`).
 *
 * Two-tier UI:
 *   - Default: a single-line summary ("Fetching instrument/TIC1105 in
 *     EventHouse…") with a chevron toggle.
 *   - Expanded: a key/value panel showing the raw frame fields (tool,
 *     status, layer, node_path) for operators debugging a stuck call.
 *
 * Distinct from OrchestrationStatus (the agent-orchestration heartbeat).
 * A tool_progress event can interleave with the orchestration status,
 * so this component renders side-by-side rather than replacing it.
 */

import { useId, useState } from 'react'
import { ChevronDown, ChevronUp } from 'lucide-react'
import { useTranslation } from '@/i18n'

import type {
  ToolProgressData,
  ToolProgressLayer,
  ToolProgressStatusKind,
} from '../../utils/toolProgressTypes'

// Re-export the shared types from the previous import path so existing
// consumers (V1 ChatInterface, useChatV2) keep working without churn.
export type {
  ToolProgressData,
  ToolProgressDetail,
  ToolProgressLayer,
  ToolProgressStatusKind,
} from '../../utils/toolProgressTypes'

interface ToolProgressStatusProps {
  data: ToolProgressData
}

const STATUS_COPY: Record<ToolProgressStatusKind, { key: string; defaultValue: string }> = {
  invoked: { key: 'chatv2:tool_progress.status_invoked', defaultValue: 'Calling' },
  binding_resolved: { key: 'chatv2:tool_progress.status_binding_resolved', defaultValue: 'Resolving' },
  fetching: { key: 'chatv2:tool_progress.status_fetching', defaultValue: 'Fetching' },
  completed: { key: 'chatv2:tool_progress.status_completed', defaultValue: 'Done' },
  failed: { key: 'chatv2:tool_progress.status_failed', defaultValue: 'Failed' },
}

// snapshot cache is a generic noun and is translated; eventhouse /
// eventstream are product proper-nouns and stay verbatim.
const LAYER_COPY: Record<ToolProgressLayer, { key?: string; defaultValue: string }> = {
  snapshot: { key: 'chatv2:tool_progress.layer_snapshot', defaultValue: 'snapshot cache' },
  eventhouse: { defaultValue: 'EventHouse' },
  eventstream: { defaultValue: 'EventStream' },
}

/** Build a "Looking up <node_path> in <layer>"-style sentence. The
 *  copy degrades gracefully when only one (or neither) of node_path /
 *  layer is provided. */
function buildDescription(
  data: ToolProgressData,
  t: (key: string, opts: Record<string, unknown>) => string,
): string {
  const statusEntry = STATUS_COPY[data.status]
  const verb = statusEntry ? t(statusEntry.key, { defaultValue: statusEntry.defaultValue }) : data.status
  const target = data.detail?.node_path
  const layerEntry = data.detail?.layer ? LAYER_COPY[data.detail.layer] : undefined
  const layer = layerEntry
    ? layerEntry.key
      ? t(layerEntry.key, { defaultValue: layerEntry.defaultValue })
      : layerEntry.defaultValue
    : undefined

  if (target && layer) return t('chatv2:tool_progress.desc_target_in_layer', { verb, target, layer, defaultValue: '{{verb}} {{target}} in {{layer}}…' })
  if (target) return t('chatv2:tool_progress.desc_target', { verb, target, defaultValue: '{{verb}} {{target}}…' })
  if (layer) return t('chatv2:tool_progress.desc_via_layer', { verb, layer, defaultValue: '{{verb}} via {{layer}}…' })
  return t('chatv2:tool_progress.desc_tool', { verb, tool: data.tool, defaultValue: '{{verb}} {{tool}}…' })
}

export function ToolProgressStatus({ data }: ToolProgressStatusProps) {
  const { t } = useTranslation()
  const [expanded, setExpanded] = useState(false)
  // Per-instance unique DOM id for `aria-controls`. Keying on
  // `data.tool` alone (the previous shape) collided when the same tool
  // fired twice in a single conversation — two `<dl>` elements would
  // share an id, breaking aria-controls and HTML uniqueness. `useId()`
  // gives each render-instance a fresh stable id.
  const detailId = `tool-progress-detail-${useId()}`
  const description = buildDescription(data, t)
  const isFailed = data.status === 'failed'
  const Chevron = expanded ? ChevronUp : ChevronDown

  return (
    <div
      role="status"
      data-testid="tool-progress-status"
      data-tool={data.tool}
      data-status={data.status}
      data-expanded={expanded ? 'true' : 'false'}
      className="flex flex-col gap-1 text-sm text-gray-500 dark:text-dark-text-muted"
    >
      <div className="flex items-center gap-2">
        {/* Animated dot — solid red when failed so the failure-clear
         *  transition is visible before the line is unmounted by the
         *  parent. */}
        <div
          className={
            isFailed
              ? 'w-2 h-2 rounded-full bg-red-500 flex-shrink-0'
              : 'w-2 h-2 rounded-full bg-blue-500 animate-pulse flex-shrink-0'
          }
        />
        <span
          data-testid="tool-progress-tool-chip"
          className="font-mono text-xs px-1.5 py-0.5 rounded bg-gray-100 dark:bg-dark-accent"
        >
          {data.tool}
        </span>
        <span className="break-words flex-1">{description}</span>
        <button
          type="button"
          onClick={() => setExpanded((prev) => !prev)}
          aria-expanded={expanded}
          aria-controls={detailId}
          aria-label={expanded
            ? t('chatv2:tool_progress.collapse_detail', { defaultValue: 'Collapse tool progress detail' })
            : t('chatv2:tool_progress.expand_detail', { defaultValue: 'Expand tool progress detail' })}
          className="flex-shrink-0 p-0.5 rounded hover:bg-gray-100 dark:hover:bg-dark-accent transition-colors"
          data-testid="tool-progress-toggle"
        >
          <Chevron className="w-4 h-4" />
        </button>
      </div>

      {/* Always render the detail panel and toggle visibility via the
       *  `hidden` attribute. ARIA authoring practices require the
       *  element referenced by `aria-controls` to be present in the
       *  DOM; conditionally mounting the `<dl>` left screen readers
       *  pointing at nothing when collapsed. `data-testid` is omitted
       *  while collapsed so existing "not in document" assertions
       *  continue to express intent. */}
      <dl
        id={detailId}
        hidden={!expanded}
        data-testid={expanded ? 'tool-progress-detail' : undefined}
        className="ml-4 grid grid-cols-[max-content_1fr] gap-x-3 gap-y-0.5 font-mono text-xs"
      >
        <dt className="text-gray-400 dark:text-gray-500">tool</dt>
        <dd className="text-gray-700 dark:text-dark-text">{data.tool}</dd>
        <dt className="text-gray-400 dark:text-gray-500">status</dt>
        <dd className="text-gray-700 dark:text-dark-text">{data.status}</dd>
        {data.detail?.layer && (
          <>
            <dt className="text-gray-400 dark:text-gray-500">layer</dt>
            <dd className="text-gray-700 dark:text-dark-text">{data.detail.layer}</dd>
          </>
        )}
        {data.detail?.node_path && (
          <>
            <dt className="text-gray-400 dark:text-gray-500">node_path</dt>
            <dd className="text-gray-700 dark:text-dark-text">{data.detail.node_path}</dd>
          </>
        )}
      </dl>
    </div>
  )
}

export default ToolProgressStatus
